import React, { useEffect, useState } from "react";
import { Meta } from "../layout/Meta";
import { SITE } from "../config/site";
import { PRODUCT_ASSETS } from "../content/productAssets";
import { productJsonLd, scrollToHash } from "../lib/productSeo";
import {
  ProductSection,
  ProductBreadcrumb,
  DisplayHeading,
  ProductArtwork,
  BenefitStrip,
  InkAppCta,
  CriteriaFlow,
  CandidateCard,
  ClosingCtaBanner,
  IllustrativeNote,
} from "../components/product";

const PATH = "/strategy-discovery";
const TITLE = "Strategy Discovery";
const DESCRIPTION =
  "Choose search criteria, compare candidate strategies, and inspect the evidence. Strategy Discovery uses a structured sample split and a Custom Score you control.";

const phone = PRODUCT_ASSETS.strategyDiscoveryPhone;
const review = PRODUCT_ASSETS.strategyDiscoveryReview;

const CANDIDATES = [
  { n: "1", title: "Trend Pullback (MA + RSI)", status: "Pass", trades: "2,488" },
  { n: "2", title: "Breakout Continuation", status: "Pass", trades: "3,112" },
  { n: "3", title: "Mean Reversion (Bollinger)", status: "Review", trades: "1,866" },
  { n: "4", title: "Opening Range Breakout", status: "Pass", trades: "4,220" },
  { n: "5", title: "Volatility Compression", status: "Review", trades: "1,733" },
];

function useDesktopReview() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}

export default function StrategyDiscoveryPage() {
  const isDesktop = useDesktopReview();

  useEffect(() => {
    scrollToHash();
  }, []);

  return (
    <div className="w-full bg-bg0">
      <Meta
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        keywords={[
          "Strategy Discovery",
          "strategy research",
          "sample split",
          "Custom Score",
          "candidate strategies",
          "Stratify Backtesting",
        ]}
        jsonLd={productJsonLd({ title: TITLE, description: DESCRIPTION, path: PATH })}
      />

      <ProductSection className="overflow-hidden pt-10 md:pt-14">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-20 -z-10 mx-auto h-72 max-w-5xl opacity-40"
        >
          <img src="/images/hero-wave.png" alt="" className="h-full w-full object-contain" />
        </div>

        <ProductBreadcrumb current="Strategy Discovery" />

        <div className="mt-8 grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <DisplayHeading>
              Discover ideas.
              <br />
              Refine the candidates.
            </DisplayHeading>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-muted">
              Choose your search criteria, compare candidates and inspect the evidence.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3">
              <InkAppCta>Get the app</InkAppCta>
              <p className="text-sm text-muted">Strategy research, anytime, anywhere.</p>
            </div>
          </div>
          <div className="min-w-0 lg:col-span-6">
            <ProductArtwork
              src={phone.src}
              width={phone.width}
              height={phone.height}
              alt={phone.alt}
              priority
              className="mx-auto max-w-sm lg:max-w-md"
            />
          </div>
        </div>

        <div className="mt-14">
          <p className="text-sm font-semibold text-ink">From many ideas to your best candidates.</p>
          <p className="mt-1 text-sm text-muted">
            Explore a wide set of strategies, then compare and refine.
          </p>
          <div className="mt-5">
            <CriteriaFlow
              steps={[
                { icon: "chart", title: "Indicators", meta: "50+ options" },
                { icon: "filter", title: "Patterns", meta: "50+ options" },
                { icon: "search", title: "Search presets", meta: "5× built-in" },
                { icon: "gauge", title: "Sample split", meta: "60 / 25 / 15" },
              ]}
            />
          </div>
        </div>

        <div className="mt-10">
          <p className="text-sm font-semibold text-ink">Example candidates</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <CandidateCard
              index="1"
              title="Mean Reversion"
              subtitle="RSI + Bollinger Bands · Standard search"
              bars={[40, 55, 35, 70, 50, 65, 45, 80, 55, 60, 48, 72]}
            />
            <CandidateCard
              index="2"
              title="Breakout"
              subtitle="Donchian Channel · Standard search"
              bars={[30, 45, 60, 40, 75, 50, 35, 68, 55, 42, 80, 58]}
            />
            <CandidateCard
              index="3"
              title="Trend Following"
              subtitle="EMA crossover · Standard search"
              bars={[50, 40, 62, 48, 55, 70, 44, 52, 78, 60, 46, 66]}
            />
          </div>
          <IllustrativeNote className="mt-3">
            Candidate charts are illustrative graphics, not market performance evidence.
          </IllustrativeNote>
        </div>

        <BenefitStrip
          className="mt-16"
          items={[
            {
              icon: "search",
              title: "Choose criteria",
              body: "Set indicators, patterns and search presets to explore a wide range of strategies.",
            },
            {
              icon: "chart",
              title: "Compare candidates",
              body: "Review key metrics, inspect the evidence and find the most promising ideas.",
            },
            {
              icon: "bookmark",
              title: "Save for review",
              body: "Keep interesting candidates and revisit them anytime.",
            },
          ]}
        />
      </ProductSection>

      <ProductSection id="structured-review">
        <DisplayHeading as="h2">
          A structured search.
          <br />
          A more deliberate review.
        </DisplayHeading>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted">
          Choose your criteria. Compare and refine candidates.
        </p>

        {isDesktop ? (
          <div className="mt-10 grid items-start gap-8 lg:grid-cols-12">
            <div className="min-w-0 lg:col-span-7">
              <ProductArtwork
                src={review.src}
                width={review.width}
                height={review.height}
                alt={review.alt}
              />
            </div>
            <div className="min-w-0 lg:col-span-5">
              <ProductArtwork
                src={phone.src}
                width={phone.width}
                height={phone.height}
                alt={phone.alt}
                className="mx-auto max-w-sm"
              />
            </div>
          </div>
        ) : (
          <div className="mt-10 space-y-6">
            <DiscoveryReviewNative />
            <ProductArtwork
              src={phone.src}
              width={phone.width}
              height={phone.height}
              alt={phone.alt}
              className="mx-auto max-w-xs"
            />
          </div>
        )}

        <IllustrativeNote className="mt-6">
          Sample split, trade counts, and status labels are an illustrative example — not live
          Discovery results.
        </IllustrativeNote>

        <div className="mt-12">
          <ClosingCtaBanner
            title="Find ideas worth investigating."
            body="Candidate strategies need further review."
          />
        </div>

        <p className="mt-8 text-xs text-muted">
          Candidate strategies need further review. Not financial advice.{" "}
          <a
            href={SITE.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-hint-deep underline-offset-4 hover:underline"
          >
            Download on the App Store
          </a>
          .
        </p>
      </ProductSection>
    </div>
  );
}

function DiscoveryReviewNative() {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-ink/8 bg-white p-5">
        <p className="text-sm font-semibold text-ink">Sample split</p>
        <div className="mt-3 flex h-3 overflow-hidden rounded-full">
          <span className="w-[60%] bg-[#3b82f6]" />
          <span className="w-[25%] bg-[#fb7185]" />
          <span className="w-[15%] bg-[#e0f2fe]" />
        </div>
        <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-3">
          <div>
            <dt className="font-semibold text-ink">Training 60%</dt>
            <dd className="mt-1 text-muted">Used to find and optimize strategies.</dd>
          </div>
          <div>
            <dt className="font-semibold text-ink">Validation 25%</dt>
            <dd className="mt-1 text-muted">Used to confirm robustness.</dd>
          </div>
          <div>
            <dt className="font-semibold text-ink">Holdout 15%</dt>
            <dd className="mt-1 text-muted">Kept holdout separate from selection.</dd>
          </div>
        </dl>
      </div>

      <div className="overflow-hidden rounded-2xl bg-ink text-white">
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-base font-semibold">Candidate strategies</h3>
          <span className="text-xs text-white/50">12 candidates</span>
        </div>
        <ul className="divide-y divide-white/8">
          {CANDIDATES.map((row) => (
            <li key={row.n} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
              <div className="min-w-0">
                <div className="truncate font-medium">
                  <span className="mr-2 text-white/40">{row.n}</span>
                  {row.title}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className={[
                      "h-2 w-2 rounded-full",
                      row.status === "Pass" ? "bg-hint-strong" : "bg-orange-400",
                    ].join(" ")}
                  />
                  {row.status}
                </span>
                <span className="w-12 text-right text-white/70">{row.trades}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <h3 className="text-base font-semibold text-ink">Set your target.</h3>
          <p className="mt-1 text-sm text-muted">
            Use a Custom Score to rank and compare candidates.
          </p>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between text-ink">
              <dt>Total return</dt>
              <dd className="text-hint-deep">40%</dd>
            </div>
            <div className="flex justify-between text-ink">
              <dt>Sharpe ratio</dt>
              <dd className="text-hint-deep">30%</dd>
            </div>
            <div className="flex justify-between text-ink">
              <dt>Max drawdown</dt>
              <dd className="text-hint-deep">20%</dd>
            </div>
            <div className="flex justify-between text-ink">
              <dt>Win rate</dt>
              <dd className="text-hint-deep">10%</dd>
            </div>
          </dl>
        </div>
        <div>
          <h3 className="text-base font-semibold text-ink">Refine your coverage.</h3>
          <p className="mt-1 text-sm text-muted">Control the building blocks of your search.</p>
          <dl className="mt-4 space-y-2 text-sm text-ink">
            <div className="flex justify-between">
              <dt>Indicators</dt>
              <dd className="text-muted">50 selected</dd>
            </div>
            <div className="flex justify-between">
              <dt>Patterns</dt>
              <dd className="text-muted">50 selected</dd>
            </div>
            <div className="flex justify-between">
              <dt>Search preset</dt>
              <dd className="text-muted">Standard Search (5×)</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
